/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useEffect, useState } from 'react';
import isNil from 'lodash/isNil';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import { TextInput } from 'carbon-addons-iot-react';
import { useDebounce } from 'use-debounce';

import * as utils from './schemaEditorUtils';

const propTypes = {
  /*
   * Formik path to the schema node that this input will show/modify the default value of, e.g.:
   *
   * For the Formik data:
   *   {
   *     schema: {
   *        content: {
   *          properties: [
   *              {
   *                  name: "root",
   *                  type: "object",
   *                  properties: [
   *                      {
   *                          name: "leaf",
   *                          "type": "string",
   *                          default: "hello"
   *                          }
   *                      }
   *                  ]
   *              }
   *          ]
   *     }
   *   }
   *
   * This input will show/modify "leaf" if it's ID is set to "schema.content.properties[0].properties[0]"
   *
   */
  id: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  syncToFormikDebounceMillis: PropTypes.number,
  placeholder: PropTypes.string,
};

const defaultProps = {
  syncToFormikDebounceMillis: 500,
  placeholder: '',
};

// TODO: some way we could reuse TextInputFormik for this?
const SchemaDefaultInput = ({ id, readOnly, syncToFormikDebounceMillis, placeholder }) => {
  const [field, meta, helpers] = useField(id);

  const [value, setValue] = useState({
    // The contents of the input field
    data: get(field, 'value.default'),

    // iff true, the value of data will be syned back to Formik state
    // once the debounce timeout expires
    syncToFormik: false,
  });

  // periodically sync changes to value back to formik state
  const [valueDebounced] = useDebounce(value, syncToFormikDebounceMillis);
  useEffect(() => {
    if (valueDebounced.syncToFormik) {
      const newDefault = utils.generateDefault(get(field, 'value.type'), valueDebounced.data);
      helpers.setValue({
        ...field.value,
        default: newDefault,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueDebounced]);

  // sync changes made to formik model back to the rendered value
  // (this occurs e.g. when the form is reset, if the user selects a different event ID in the PI editor, if the user removes a row from the schema editor table, etc)
  useEffect(() => {
    // console.log('sync from formik -> rendered: ', field.value)
    setValue({
      data: get(field, 'value.default'),
      // here we are syncing an update from formik to our locally-rendered value
      // don't propagate this change back to Formik when the debounce timeout expires
      syncToFormik: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  if (isNil(field.value) || field.value === '') {
    return null;
  }

  // if value is an object or an array, the user has set it via the API or by source view
  // in this case, just render it and do not provide an input
  // if (isPlainObject(field.value.default) || isArray(field.value.default)) {
  //     return (
  //         <CodeSnippet
  //             type="inline"
  //         >
  //             {JSON.stringify(field.value.default)}
  //         </CodeSnippet>
  //     )
  // }
  // do not provide a default value input for object/arrays (for now: this may change)
  if (
    utils
      .ensureArray(field.value.type)
      .some(
        t => t === utils.JSON_SCHEMA_DATATYPES.object || t === utils.JSON_SCHEMA_DATATYPES.array
      )
  ) {
    return null;
  }

  // render null as the string "null" and undefined as '' to ensure
  // component remains in "fully controlled" state
  const valueToRender =
    value.data === null ? 'null' : value.data === undefined ? '' : `${value.data}`; // cast to string

  // can't use TextInputFormik here, since we need to customize value rendering and onChange
  return (
    <TextInput
      id={id}
      name={id}
      value={valueToRender}
      onChange={e => {
        // in this case, the change was made by the user modifying the input field's contents
        // so we want to sync the change back to formik state
        setValue({
          data: e.target.value,
          syncToFormik: true,
        });
      }}
      onBlur={field.onBlur}
      invalid={get(meta, 'error.default') && meta.touched}
      invalidText={get(meta, 'error.default')}
      disabled={readOnly}
      labelText=""
      hideLabel
      placeholder={placeholder}
    />
  );
};

SchemaDefaultInput.propTypes = propTypes;
SchemaDefaultInput.defaultProps = defaultProps;

export default SchemaDefaultInput;

/* Displaying a type-specific input is tricky when the user has specified >1 data type for the column
 * for now, we'll just use a TextInput for the default value in all cases.
 *
 * The value entered by the user will be cast to one of the selected type(s) automatically by the UI
 *
 * Options to explore in the future:
 *  - have a control in the column itself that allows you to cycle through the input types in the table?
 *  - move default value input to a dialog in this cases, activated by a button in the Default Value column?
 * -  just always use a text input and rely on form validation later to ensure they've put in a compatible value?
 *
 *
 * Example code for how we might implement datatype-specific default value input components:
 */
/*
const typeValue =  get(value, `properties[${propertyIndex}].type`);

switch(typeValue) {
  case 'string': return (
    <TableTextInput
      contentFieldId={contentFieldId}
      value={value}
      propertyIndex={propertyIndex}
      fieldName='default'
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      disabled={disabled}
    />
  )
  case 'boolean': return (
    <TableToggle
      contentFieldId={contentFieldId}
      value={value}
      propertyIndex={propertyIndex}
      fieldName='default'
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      disabled={disabled}
    />
  )
  case 'number': return (
    <TableNumberInput
      contentFieldId={contentFieldId}
      value={value}
      propertyIndex={propertyIndex}
      fieldName='default'
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      disabled={disabled}
    />
  )
  default: return null;
}
*/
